// Safe formatting utilities to prevent runtime errors

/**
 * Safely format a number as currency
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'HKD')
 * @param {string} locale - Locale (default: 'en-HK')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'HKD', locale = 'en-HK') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `HK$0`;
  }
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    return `HK$${(parseFloat(amount) || 0).toLocaleString()}`;
  }
};

/**
 * Safely format a number with toLocaleString
 * @param {number} value - The value to format
 * @param {string} locale - Locale (default: 'en-HK')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, locale = 'en-HK') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  try {
    return parseFloat(value).toLocaleString(locale);
  } catch (error) {
    return '0';
  }
};

/**
 * Safely format a percentage
 * @param {number} value - The value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  try {
    return `${(parseFloat(value) || 0).toFixed(decimals)}%`;
  } catch (error) {
    return '0%';
  }
};

/**
 * Safely format a date
 * @param {string|Date} date - The date to format
 * @param {string} locale - Locale (default: 'en-HK')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, locale = 'en-HK') => {
  if (!date) {
    return '-';
  }
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    return dateObj.toLocaleDateString(locale);
  } catch (error) {
    return '-';
  }
};

/**
 * Safely format a date and time
 * @param {string|Date} date - The date to format
 * @param {string} locale - Locale (default: 'en-HK')
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, locale = 'en-HK') => {
  if (!date) {
    return '-';
  }
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    return dateObj.toLocaleString(locale);
  } catch (error) {
    return '-';
  }
};
